import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Send, Sparkles, Cpu, FileText, Download, ShieldCheck,
  Loader2, ArrowRight,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { notifyWorkspaceChanged, NEW_CHAT_EVENT } from "@/lib/workspaceProjects";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PATTERN_LABEL = {
  simple_answer: "Direct answer",
  research: "Research",
  artifact: "Artifact",
  optimization: "Optimization handoff",
};

export default function ChatArea({ sub }) {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState(sub || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [decision, setDecision] = useState(null);
  const [outputType, setOutputType] = useState("auto");
  const scrollRef = useRef(null);

  const scrollBottom = () => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  };

  // The left rail drives which chat is open (via the URL). A bare /app/chat = new chat.
  useEffect(() => { setActiveId(sub || null); setDecision(null); if (!sub) { setMessages([]); setInput(""); } }, [sub]);

  const newChat = useCallback(() => { setActiveId(null); setMessages([]); setInput(""); setDecision(null); }, []);
  useEffect(() => {
    window.addEventListener(NEW_CHAT_EVENT, newChat);
    return () => window.removeEventListener(NEW_CHAT_EVENT, newChat);
  }, [newChat]);

  const loadMessages = useCallback(async (id) => {
    if (!id) { setMessages([]); return; }
    try {
      const { data } = await axios.get(`${API}/workspace/conversations/${id}/messages`);
      setMessages(data.messages || []);
      scrollBottom();
    } catch { /* keep existing local messages on a transient load failure */ }
  }, []);

  useEffect(() => { loadMessages(activeId); }, [activeId, loadMessages]);


  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setBusy(true);
    setInput("");
    setDecision(null);
    setStreamText("");
    // optimistic user bubble
    setMessages((m) => [...m, { message_id: `tmp_${Date.now()}`, role: "user", content: text }]);
    scrollBottom();

    let convId = activeId;
    let assistantBuffer = "";
    let handoff = null;
    let artifact = null;
    let unavailable = null;
    let citations = [];

    try {
      const res = await fetch(`${API}/workspace/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text, conversation_id: activeId || undefined,
          output_type: outputType !== "auto" ? outputType : undefined }),
      });
      if (!res.ok || !res.body) throw new Error("chat_failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const frames = buf.split("\n\n");
        buf = frames.pop() || "";
        for (const frame of frames) {
          const lines = frame.split("\n");
          const ev = (lines.find((l) => l.startsWith("event:")) || "").slice(6).trim();
          const dataLine = lines.find((l) => l.startsWith("data:"));
          if (!dataLine) continue;
          let payload = {};
          try { payload = JSON.parse(dataLine.slice(5).trim()); } catch { continue; }
          if (ev === "meta") { convId = payload.conversation_id; }
          else if (ev === "title") { notifyWorkspaceChanged(); }
          else if (ev === "decision") { setDecision(payload); }
          else if (ev === "token") { assistantBuffer += payload.content || ""; setStreamText(assistantBuffer); scrollBottom(); }
          else if (ev === "handoff") { handoff = payload; }
          else if (ev === "artifact") { artifact = payload; }
          else if (ev === "sources") { citations = payload.citations || []; }
          else if (ev === "unavailable") { unavailable = payload; }
        }
      }
    } catch {
      unavailable = { message: "The chat service is temporarily unavailable." };
    }

    // finalize assistant message locally, then refresh from server for canonical truth
    const meta = [];
    if (handoff) meta.push({ type: "optimization_handoff", ...handoff });
    if (artifact) meta.push({ type: "artifact", ...artifact });
    if (citations.length) meta.push({ type: "answer", citations, ...(decision || {}) });
    const finalText = handoff?.message || (artifact ? "Composed a document artifact." : "") ||
      assistantBuffer || unavailable?.message || "";
    setMessages((m) => [...m, { message_id: `tmpA_${Date.now()}`, role: "assistant", content: finalText, meta }]);
    setStreamText("");
    setBusy(false);
    notifyWorkspaceChanged();
    if (!activeId && convId) { setActiveId(convId); navigate(`/app/chat/${convId}`); }
    if (convId) await loadMessages(convId);
    scrollBottom();
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const downloadArtifact = async (runId, artifactId, ext) => {
    try {
      const res = await fetch(`${API}/workspace/artifacts/${runId}/download/${artifactId}`,
        { credentials: "include" });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${artifactId}.${ext || "bin"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* noop */ }
  };

  return (
    <div className="h-screen flex" data-testid="chat-area">
      {/* Conversation pane */}
      <div className="flex-1 flex flex-col">
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6" data-testid="chat-messages">
          {messages.length === 0 && !streamText && (
            <div className="h-full flex flex-col items-center justify-center text-center max-w-lg mx-auto">
              <div className="w-14 h-14 rounded-2xl bg-[var(--qn-primary-muted)] flex items-center justify-center mb-4">
                <Sparkles className="w-7 h-7 text-[var(--qn-primary)]" />
              </div>
              <h2 className="text-2xl font-semibold mb-2">How can QNNEAL help today?</h2>
              <p className="text-sm text-[var(--qn-foreground-muted)]">
                Ask a question, research a topic, create a document, or start an optimization —
                QNNEAL picks the smallest sufficient approach and governs the model for you.
              </p>
            </div>
          )}

          <div className="max-w-3xl mx-auto space-y-5">
            {messages.map((m) => (
              <MessageBubble key={m.message_id} m={m} onOpenTask={(taskId) =>
                navigate("/app/optimization", { state: { taskId } })}
                onDownload={downloadArtifact} />
            ))}

            {streamText && (
              <div data-testid="chat-streaming-bubble">
                {decision && <ModelBadge decision={decision} />}
                <div className="prose prose-sm dark:prose-invert max-w-none text-[var(--qn-foreground)]">
                  <ReactMarkdown>{streamText}</ReactMarkdown>
                </div>
              </div>
            )}
            {busy && !streamText && (
              <div className="flex items-center gap-2 text-sm text-[var(--qn-foreground-muted)]" data-testid="chat-thinking">
                <Loader2 className="w-4 h-4 animate-spin" /> Working…
              </div>
            )}
          </div>
        </div>

        {/* Composer */}
        <div className="border-t border-[var(--qn-border)] bg-[var(--qn-background)] p-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[11px] text-[var(--qn-foreground-subtle)]">Output:</span>
              <Select value={outputType} onValueChange={setOutputType}>
                <SelectTrigger data-testid="chat-output-type" className="h-8 w-44 text-xs bg-[var(--qn-card)] border-[var(--qn-border)]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto</SelectItem>
                  <SelectItem value="document">Word document</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="presentation">Presentation</SelectItem>
                  <SelectItem value="spreadsheet">Spreadsheet</SelectItem>
                  <SelectItem value="chart">Chart</SelectItem>
                  <SelectItem value="image">Image</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2 rounded-2xl border border-[var(--qn-border)] bg-[var(--qn-card)] px-3 py-2 focus-within:border-[var(--qn-primary)] transition-colors">
              <textarea
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                rows={1}
                placeholder="Message QNNEAL…"
                className="flex-1 bg-transparent resize-none outline-none text-sm py-1.5 max-h-40 text-[var(--qn-foreground)] placeholder:text-[var(--qn-foreground-subtle)]"
              />
              <Button onClick={send} disabled={busy || !input.trim()} data-testid="chat-send"
                size="icon" className="rounded-xl bg-[var(--qn-primary)] hover:opacity-90 text-white shrink-0">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
            <div className="text-[11px] text-[var(--qn-foreground-subtle)] text-center mt-2">
              QNNEAL governs model selection, data classification and cost. Optimization requests move to the governed Optimization workspace.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModelBadge({ decision }) {
  const provider = decision.provider || "";
  const model = decision.model || "";
  const tier = decision.tier || "";
  return (
    <div className="flex items-center gap-2 mb-2 text-[11px] text-[var(--qn-foreground-subtle)]" data-testid="chat-model-badge">
      <ShieldCheck className="w-3.5 h-3.5 text-[var(--qn-primary)]" />
      <span className="font-mono">{[provider, model].filter(Boolean).join(" · ")}</span>
      {tier && (
        <span className="px-1.5 py-0.5 rounded bg-[var(--qn-surface)] border border-[var(--qn-border)]">
          {PATTERN_LABEL[tier] || tier}
        </span>
      )}
      <span className="italic">governed</span>
    </div>
  );
}

function MessageBubble({ m, onOpenTask, onDownload }) {
  const isUser = m.role === "user";
  const meta = m.meta || [];
  const handoff = meta.find((x) => x.type === "optimization_handoff");
  const artifact = meta.find((x) => x.type === "artifact");
  const answer = meta.find((x) => x.type === "answer");
  const badge = answer || (artifact && artifact.model ? artifact : null);
  const citations = (answer && answer.citations) || [];

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}`}>
      <div className={`max-w-[85%] ${isUser ? "" : "w-full"}`}>
        {!isUser && badge && <ModelBadge decision={badge} />}
        <div className={`rounded-2xl px-4 py-2.5 text-sm ${
          isUser
            ? "bg-[var(--qn-primary)] text-white"
            : "bg-[var(--qn-card)] border border-[var(--qn-border)] text-[var(--qn-foreground)]"}`}>
          {isUser ? (
            <div className="whitespace-pre-wrap">{m.content}</div>
          ) : (
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <ReactMarkdown>{m.content || ""}</ReactMarkdown>
            </div>
          )}
        </div>

        {!isUser && citations.length > 0 && (
          <div className="mt-2 rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-3" data-testid="chat-citations">
            <div className="text-[11px] uppercase tracking-wider text-[var(--qn-foreground-subtle)] mb-1.5">Sources</div>
            <div className="space-y-1.5">
              {citations.map((c, i) => (
                <div key={i} data-testid={`chat-citation-${i}`} className="text-xs text-[var(--qn-foreground-muted)]">
                  <span className="font-mono text-[var(--qn-primary)]">[S{i + 1}]</span> {c.title}
                  <span className="text-[var(--qn-foreground-subtle)]"> · {c.location} · v{c.version}</span>
                  <div className="text-[11px] text-[var(--qn-foreground-subtle)] mt-0.5 italic">{c.passage}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {handoff && (
          <div className="mt-2 rounded-xl border border-[var(--qn-primary)]/40 bg-[var(--qn-primary-muted)] p-3" data-testid="chat-optimization-handoff-card">
            <div className="flex items-center gap-2 text-[var(--qn-primary)] font-medium text-sm mb-1">
              <Cpu className="w-4 h-4" /> Optimization Task drafted
            </div>
            <div className="text-xs text-[var(--qn-foreground-muted)] mb-2">
              Nothing has been executed. Review inputs, policy and constraints in the governed Optimization workspace.
            </div>
            <Button size="sm" data-testid="chat-open-optimization"
              onClick={() => onOpenTask(handoff.task_id)}
              className="gap-1.5 bg-[var(--qn-primary)] text-white hover:opacity-90">
              Open Optimization workspace <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}

        {artifact && (
          <div className="mt-2 rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-3" data-testid="chat-artifact-card">
            <div className="flex items-center gap-2 text-sm font-medium mb-1">
              <FileText className="w-4 h-4 text-[var(--qn-primary)]" /> {(artifact.output_type || "document")} artifact
            </div>
            <div className="text-[11px] text-[var(--qn-foreground-subtle)] mb-2 font-mono">
              source: markdown · export: {(artifact.export_format || "").toUpperCase()} · v{artifact.version || 1} · {artifact.classification || "confidential"}
            </div>
            {artifact.preview && (
              <div className="text-xs text-[var(--qn-foreground-muted)] max-h-40 overflow-y-auto border border-[var(--qn-border)] rounded p-2 mb-2 whitespace-pre-wrap">
                {artifact.preview.slice(0, 1200)}
              </div>
            )}
            <div className="flex gap-2">
              <Button size="sm" variant="outline" data-testid="chat-download-source"
                onClick={() => onDownload(artifact.run_id, artifact.source_artifact_id, "md")} className="gap-1.5">
                <Download className="w-3.5 h-3.5" /> Editable source
              </Button>
              <Button size="sm" variant="outline" data-testid="chat-download-render"
                onClick={() => onDownload(artifact.run_id, artifact.render_artifact_id, artifact.export_format)} className="gap-1.5">
                <Download className="w-3.5 h-3.5" /> {(artifact.export_format || "export").toUpperCase()} export
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
